import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { projectModuleType } from "../../Redux/Slice/Day";

const Projecttypemoduleanalysis = () => {
  const dispatch = useDispatch();

  const { projectModuleTypeData = {} } = useSelector(
    (state) => state.Day || {}
  );

  const projects = projectModuleTypeData?.data || [];

  const [openProject, setOpenProject] = useState(null);
  const [openType, setOpenType] = useState(null);
  const [openModule, setOpenModule] = useState(null);
  const [activeComplaints, setActiveComplaints] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [viewMode, setViewMode] = useState("hierarchy");

  useEffect(() => {
    dispatch(projectModuleType());
  }, [dispatch]);

  // Helper function to extract company name from data
  const extractCompanyName = (companyData) => {
    if (!companyData) return "Unknown Company";
    
    if (typeof companyData === 'string') {
      return companyData;
    }
    
    if (typeof companyData === 'object') {
      const name = 
        companyData.WhaClientName ||
        companyData.companyName ||
        companyData.CompanyName ||
        companyData.name ||
        companyData.AdminID ||
        companyData._id;
      
      if (name) {
        if (typeof name === 'string') return name;
        if (typeof name === 'object') return JSON.stringify(name).substring(0, 30) + "...";
      }
      
      return "Unknown Company";
    }
    
    return "Unknown Company";
  };

  // Helper function to get company name from complaint
  const getCompanyName = (complaint) => {
    if (!complaint) return "Unknown Company";
    
    let companyData;
    
    if (complaint.id && typeof complaint.id === 'object') {
      companyData = complaint.id.CompanyName;
    } else if (complaint.CompanyName) {
      companyData = complaint.CompanyName;
    } else if (complaint.id && typeof complaint.id === 'object' && complaint.id.WhaClientName) {
      return complaint.id.WhaClientName;
    } else if (complaint.WhaClientName) {
      return complaint.WhaClientName;
    }
    
    return extractCompanyName(companyData);
  };

  // Extract all unique companies from the data
  const allCompanies = useMemo(() => {
    const companySet = new Set();
    
    projects.forEach(project => {
      project.Type_id?.forEach(typeItem => {
        const type = typeItem.id;
        if (!type) return;
        
        type.module_id?.forEach(moduleItem => {
          const module = moduleItem.id;
          if (!module) return;
          
          module.ConplainBox_id?.forEach(complaint => {
            const companyName = getCompanyName(complaint);
            if (companyName && companyName !== "Unknown Company") {
              companySet.add(companyName);
            }
          });
        });
      });
    });
    
    return Array.from(companySet).sort();
  }, [projects]);

  // Filter projects based on selected company
  const filteredProjects = useMemo(() => {
    if (selectedCompany === "all") {
      return projects;
    }

    return projects.filter(project => {
      return project.Type_id?.some(typeItem => {
        const type = typeItem.id;
        if (!type) return false;

        return type.module_id?.some(moduleItem => {
          const module = moduleItem.id;
          if (!module) return false;

          return module.ConplainBox_id?.some(complaint => {
            const companyName = getCompanyName(complaint);
            return companyName === selectedCompany;
          });
        });
      });
    });
  }, [projects, selectedCompany]);

  // Count calls for selected company
  const companyCallCount = useMemo(() => {
    if (selectedCompany === "all") {
      return projects.reduce((total, project) => 
        total + (project.ConplainBox_id?.length || 0), 0
      );
    }

    let count = 0;
    projects.forEach(project => {
      project.Type_id?.forEach(typeItem => {
        const type = typeItem.id;
        if (!type) return;

        type.module_id?.forEach(moduleItem => {
          const module = moduleItem.id;
          if (!module) return;

          module.ConplainBox_id?.forEach(complaint => {
            const companyName = getCompanyName(complaint);
            if (companyName === selectedCompany) {
              count++;
            }
          });
        });
      });
    });
    return count;
  }, [projects, selectedCompany]);

  const toggleComplaint = (complaintId) => {
    setActiveComplaints(prev => 
      prev.includes(complaintId) 
        ? prev.filter(id => id !== complaintId) 
        : [...prev, complaintId]
    );
  };

  // Get complaints for a module filtered by selected company
  const getFilteredComplaints = (complaints) => {
    if (selectedCompany === "all") {
      return complaints || [];
    }
    
    return (complaints || []).filter(complaint => {
      const companyName = getCompanyName(complaint);
      return companyName === selectedCompany;
    });
  };

  // Get complaint details with all information
  const getComplaintDetails = (complaint) => {
    let complaintData = complaint;
    let companyName = "Unknown Company";
    let callNo = "N/A";
    let date = "Date not available";
    let id = complaint?._id || `missing-${Math.random()}`;
    let hasDetails = false;

    if (complaint && typeof complaint === 'object') {
      if (complaint.id && typeof complaint.id === 'object') {
        complaintData = complaint.id;
        hasDetails = true;
      } else if (complaint._id) {
        complaintData = complaint;
        hasDetails = true;
      }
    }

    if (complaintData) {
      companyName = getCompanyName(complaint);
      callNo = complaintData.CallNo || complaintData.callNo || "N/A";
      date = complaintData.Date || complaintData.date || complaintData.createdAt || "Date not available";
      id = complaintData._id || id;
      
      if (complaintData.CompanyName && typeof complaintData.CompanyName === 'object') {
        const companyObj = complaintData.CompanyName;
        if (companyObj.WhaClientName) {
          companyName = companyObj.WhaClientName;
        }
      }
    }

    return {
      id,
      callNo,
      companyName,
      date,
      hasDetails,
      rawData: complaintData,
    };
  };

  // Company-wise view
  const CompanyView = () => {
    const companyProjectsMap = useMemo(() => {
      const map = {};
      
      allCompanies.forEach(company => {
        const companyProjects = projects.filter(project => {
          return project.Type_id?.some(typeItem => {
            const type = typeItem.id;
            if (!type) return false;
            return type.module_id?.some(moduleItem => {
              const module = moduleItem.id;
              if (!module) return false;
              return module.ConplainBox_id?.some(complaint => {
                return getCompanyName(complaint) === company;
              });
            });
          });
        });

        const totalCalls = companyProjects.reduce((count, project) => {
          project.Type_id?.forEach(typeItem => {
            const type = typeItem.id;
            if (!type) return;
            type.module_id?.forEach(moduleItem => {
              const module = moduleItem.id;
              if (!module) return;
              module.ConplainBox_id?.forEach(complaint => {
                if (getCompanyName(complaint) === company) {
                  count++;
                }
              });
            });
          });
          return count;
        }, 0);

        map[company] = { projects: companyProjects, totalCalls };
      });
      
      return map;
    }, [projects, allCompanies]);

    return (
      <div style={styles.companyViewContainer}>
        {allCompanies.map(company => {
          const { projects: companyProjects, totalCalls } = companyProjectsMap[company] || { projects: [], totalCalls: 0 };
          
          return (
            <div key={company} style={styles.companyCard}>
              <div style={styles.companyHeader}>
                <span style={styles.companyIcon}>🏢</span>
                <div style={styles.companyHeaderContent}>
                  <span style={styles.companyName}>{company}</span>
                  <div style={styles.companySubtitle}>{totalCalls} total calls</div>
                </div>
                <span style={styles.companyBadge}>{companyProjects.length} projects</span>
              </div>
              <div style={styles.companyProjects}>
                {companyProjects.length > 0 ? (
                  companyProjects.map(project => {
                    const projectCallCount = project.Type_id?.reduce((count, typeItem) => {
                      const type = typeItem.id;
                      if (!type) return count;
                      return count + (type.module_id?.reduce((mCount, moduleItem) => {
                        const module = moduleItem.id;
                        if (!module) return mCount;
                        return mCount + (module.ConplainBox_id?.filter(c => 
                          getCompanyName(c) === company
                        ).length || 0);
                      }, 0) || 0);
                    }, 0) || 0;

                    return (
                      <div key={project._id} style={styles.projectItem}>
                        <div style={styles.projectItemContent}>
                          <span style={styles.projectIcon}>📁</span>
                          <div>
                            <span style={styles.projectName}>{project.project_name}</span>
                            <div style={styles.projectId}>ID: {project._id}</div>
                          </div>
                        </div>
                        <span style={styles.projectCalls}>
                          {projectCallCount} calls
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div style={styles.noData}>
                    <span style={styles.noDataIcon}>📭</span>
                    <span style={styles.noDataText}>No projects found for this company</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Hierarchical view
  const HierarchyView = () => (
    <div style={styles.projectsContainer}>
      {filteredProjects.map((project) => (
        <div key={project._id} style={styles.projectCard}>
          {/* PROJECT HEADER */}
          <div
            style={{
              ...styles.projectHeader,
              ...(openProject === project._id ? styles.projectHeaderActive : {})
            }}
            onClick={() =>
              setOpenProject(
                openProject === project._id ? null : project._id
              )
            }
          >
            <div style={styles.headerContent}>
              <div style={styles.iconWithText}>
                <span style={styles.projectIcon}>📁</span>
                <div>
                  <span style={styles.projectName}>{project.project_name}</span>
                  <div style={styles.projectId}>ID: {project._id}</div>
                </div>
              </div>
              <div style={styles.headerRight}>
                <span style={styles.badge}>
                  {project.ConplainBox_id?.length || 0} calls
                </span>
                <span style={styles.arrowIcon}>
                  {openProject === project._id ? '▼' : '▶'}
                </span>
              </div>
            </div>
          </div>

          {/* TYPES */}
          {openProject === project._id && (
            <div style={styles.childrenContainer}>
              {project.Type_id?.length > 0 ? (
                project.Type_id.map((typeItem) => {
                  const type = typeItem.id;
                  if (!type) return null;

                  return (
                    <div key={type._id} style={styles.typeCard}>
                      <div
                        style={{
                          ...styles.typeHeader,
                          ...(openType === type._id ? styles.typeHeaderActive : {})
                        }}
                        onClick={() =>
                          setOpenType(
                            openType === type._id ? null : type._id
                          )
                        }
                      >
                        <div style={styles.headerContent}>
                          <div style={styles.iconWithText}>
                            <span style={styles.typeIcon}>🏷️</span>
                            <div>
                              <span style={styles.typeName}>{type.Type_name}</span>
                              <div style={styles.typeId}>ID: {type._id}</div>
                            </div>
                          </div>
                          <div style={styles.headerRight}>
                            <span style={styles.badgeSmall}>
                              {type.ConplainBox_id?.length || 0} calls
                            </span>
                            <span style={styles.arrowIcon}>
                              {openType === type._id ? '▼' : '▶'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* MODULES */}
                      {openType === type._id && (
                        <div style={styles.childrenContainer}>
                          {type.module_id?.length > 0 ? (
                            type.module_id.map((moduleItem) => {
                              const module = moduleItem.id;
                              if (!module) return null;

                              const filteredComplaints = getFilteredComplaints(module.ConplainBox_id);

                              return (
                                <div key={module._id} style={styles.moduleCard}>
                                  <div
                                    style={{
                                      ...styles.moduleHeader,
                                      ...(openModule === module._id ? styles.moduleHeaderActive : {})
                                    }}
                                    onClick={() =>
                                      setOpenModule(
                                        openModule === module._id
                                          ? null
                                          : module._id
                                      )
                                    }
                                  >
                                    <div style={styles.headerContent}>
                                      <div style={styles.iconWithText}>
                                        <span style={styles.moduleIcon}>📦</span>
                                        <div>
                                          <span style={styles.moduleName}>{module.module_name}</span>
                                          <div style={styles.moduleId}>ID: {module._id}</div>
                                        </div>
                                      </div>
                                      <div style={styles.headerRight}>
                                        <span style={styles.badgeSmall}>
                                          {filteredComplaints.length} calls
                                        </span>
                                        <span style={styles.arrowIcon}>
                                          {openModule === module._id ? '▼' : '▶'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* COMPLAINTS */}
                                  {openModule === module._id && (
                                    <div style={styles.complaintsContainer}>
                                      <div style={styles.complaintsHeader}>
                                        <span style={styles.complaintsTitle}>📞 Call Details</span>
                                        <span style={styles.complaintsCount}>
                                          {filteredComplaints.length} calls
                                          {selectedCompany !== "all" && ` for ${selectedCompany}`}
                                        </span>
                                      </div>
                                      
                                      {filteredComplaints.length > 0 ? (
                                        <div style={styles.complaintsList}>
                                          {filteredComplaints.map((complaint, index) => {
                                            const complaintDetails = getComplaintDetails(complaint);
                                            const isActive = activeComplaints.includes(complaintDetails.id);
                                            
                                            return (
                                              <div
                                                key={complaintDetails.id}
                                                style={{
                                                  ...styles.complaintCard,
                                                  ...(isActive ? styles.complaintCardActive : {}),
                                                }}
                                                onClick={() => toggleComplaint(complaintDetails.id)}
                                              >
                                                <div style={styles.complaintHeader}>
                                                  <div style={styles.complaintIconText}>
                                                    <span style={styles.complaintIcon}>📞</span>
                                                    <div>
                                                      <span style={styles.complaintCallNo}>
                                                        Call No: {complaintDetails.callNo}
                                                      </span>
                                                      <div style={styles.companyNameSmall}>
                                                        Company: {complaintDetails.companyName}
                                                      </div>
                                                    </div>
                                                  </div>
                                                  <span style={styles.arrowIconSmall}>
                                                    {isActive ? '▼' : '▶'}
                                                  </span>
                                                </div>
                                                
                                                {isActive && (
                                                  <div style={styles.complaintDetails}>
                                                    <div style={styles.detailRow}>
                                                      <span style={styles.detailLabel}>Call No:</span>
                                                      <span style={styles.detailValue}>{complaintDetails.callNo}</span>
                                                    </div>
                                                    <div style={styles.detailRow}>
                                                      <span style={styles.detailLabel}>Company:</span>
                                                      <span style={styles.detailValue}>
                                                        {complaintDetails.companyName}
                                                      </span>
                                                    </div>
                                                    <div style={styles.detailRow}>
                                                      <span style={styles.detailLabel}>Date:</span>
                                                      <span style={styles.detailValue}>
                                                        {new Date(complaintDetails.date).toLocaleDateString()}
                                                      </span>
                                                    </div>
                                                    {complaintDetails.rawData?.Requirement && (
                                                      <div style={styles.detailRow}>
                                                        <span style={styles.detailLabel}>Requirement:</span>
                                                        <span style={styles.detailValue}>
                                                          {complaintDetails.rawData.Requirement}
                                                        </span>
                                                      </div>
                                                    )}
                                                    {complaintDetails.rawData?.Solution && (
                                                      <div style={styles.detailRow}>
                                                        <span style={styles.detailLabel}>Solution:</span>
                                                        <span style={styles.detailValue}>
                                                          {complaintDetails.rawData.Solution}
                                                        </span>
                                                      </div>
                                                    )}
                                                    {complaintDetails.rawData?.Status && (
                                                      <div style={styles.detailRow}>
                                                        <span style={styles.detailLabel}>Status:</span>
                                                        <span style={styles.detailValue}>
                                                          {complaintDetails.rawData.Status}
                                                        </span>
                                                      </div>
                                                    )}
                                                    <div style={styles.complaintFooter}>
                                                      <span style={styles.indexBadge}>
                                                        #{index + 1}
                                                      </span>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ) : (
                                        <div style={styles.noComplaints}>
                                          <span style={styles.noComplaintsIcon}>📭</span>
                                          <span style={styles.noComplaintsText}>
                                            {selectedCompany === "all" 
                                              ? "No calls found for this module" 
                                              : `No calls found for ${selectedCompany} in this module`
                                            }
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div style={styles.noData}>
                              <span style={styles.noDataIcon}>📭</span>
                              <span style={styles.noDataText}>No modules found</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={styles.noData}>
                  <span style={styles.noDataIcon}>🏷️</span>
                  <span style={styles.noDataText}>No types found for this project</span>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📊 Project → Type → Module Analysis</h2>
        <div style={styles.subtitle}>Company-wise call analysis</div>
        
        {/* Company Filter */}
        <div style={styles.filterSection}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>
              <span style={styles.filterIcon}>🏢</span>
              Select Company:
            </label>
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              style={styles.companySelect}
            >
              <option value="all">All Companies ({allCompanies.length})</option>
              {allCompanies.map(company => (
                <option key={company} value={company}>
                  {company}
                </option>
              ))}
            </select>
          </div>
          
          <div style={styles.viewToggle}>
            <button
              style={{
                ...styles.viewButton,
                ...(viewMode === 'hierarchy' ? styles.viewButtonActive : {})
              }}
              onClick={() => setViewMode('hierarchy')}
            >
              📊 Hierarchy View
            </button>
            <button
              style={{
                ...styles.viewButton,
                ...(viewMode === 'company-view' ? styles.viewButtonActive : {})
              }}
              onClick={() => setViewMode('company-view')}
            >
              🏢 Company View
            </button>
          </div>
        </div>
        
        {/* Statistics */}
        <div style={styles.statsContainer}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📁</div>
            <div style={styles.statContent}>
              <div style={styles.statValue}>{filteredProjects.length}</div>
              <div style={styles.statLabel}>Projects</div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📞</div>
            <div style={styles.statContent}>
              <div style={styles.statValue}>{companyCallCount}</div>
              <div style={styles.statLabel}>
                {selectedCompany === "all" ? "Total Calls" : `${selectedCompany} Calls`}
              </div>
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>🏢</div>
            <div style={styles.statContent}>
              <div style={styles.statValue}>{allCompanies.length}</div>
              <div style={styles.statLabel}>Companies</div>
            </div>
          </div>
        </div>
        
        {/* Current Selection */}
        {selectedCompany !== "all" && (
          <div style={styles.selectionInfo}>
            <span style={styles.selectionIcon}>✅</span>
            <span style={styles.selectionText}>
              Showing calls for: <strong>{selectedCompany}</strong>
            </span>
            <button 
              onClick={() => setSelectedCompany("all")}
              style={styles.clearSelectionButton}
            >
              Show All Companies
            </button>
          </div>
        )}
      </div>

      {filteredProjects.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🏢</div>
          <h3 style={styles.emptyTitle}>No Calls Found</h3>
          <p style={styles.emptyText}>
            {selectedCompany === "all" 
              ? "No project data available" 
              : `No calls found for company "${selectedCompany}"`
            }
          </p>
          {selectedCompany !== "all" && (
            <button 
              onClick={() => setSelectedCompany("all")}
              style={styles.showAllButton}
            >
              Show All Companies
            </button>
          )}
        </div>
      ) : viewMode === 'company-view' ? (
        <CompanyView />
      ) : (
        <HierarchyView />
      )}
    </div>
  );
};

export default Projecttypemoduleanalysis;

/* ---------------- COMPLETE STYLES ---------------- */
const styles = {
  container: {
    padding: "24px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "30px",
    padding: "20px",
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  },
  title: {
    margin: "0 0 8px 0",
    color: "#2d3436",
    fontSize: "28px",
    fontWeight: "700",
  },
  subtitle: {
    color: "#636e72",
    fontSize: "15px",
    marginBottom: "20px",
  },
  // Filter Section Styles
  filterSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "15px",
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    minWidth: "300px",
  },
  filterLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: "600",
    color: "#2c3e50",
    fontSize: "16px",
    whiteSpace: "nowrap",
  },
  filterIcon: {
    fontSize: "20px",
  },
  companySelect: {
    flex: 1,
    padding: "10px 15px",
    border: "2px solid #3498db",
    borderRadius: "8px",
    fontSize: "16px",
    outline: "none",
    background: "white",
    minWidth: "250px",
  },
  viewToggle: {
    display: "flex",
    gap: "10px",
  },
  viewButton: {
    padding: "10px 20px",
    background: "#f8f9fa",
    border: "2px solid #e9ecef",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    transition: "all 0.3s ease",
  },
  viewButtonActive: {
    background: "#3498db",
    color: "white",
    borderColor: "#2980b9",
  },
  // Stats Section Styles
  statsContainer: {
    display: "flex",
    gap: "15px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  statCard: {
    flex: 1,
    minWidth: "150px",
    background: "#f8f9fa",
    padding: "15px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    gap: "15px",
    border: "1px solid #e9ecef",
  },
  statIcon: {
    fontSize: "24px",
    background: "#3498db",
    color: "white",
    width: "50px",
    height: "50px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#2c3e50",
  },
  statLabel: {
    fontSize: "14px",
    color: "#7f8c8d",
  },
  // Selection Info Styles
  selectionInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 15px",
    background: "#e8f6f3",
    borderRadius: "8px",
    border: "1px solid #27ae60",
  },
  selectionIcon: {
    fontSize: "20px",
  },
  selectionText: {
    flex: 1,
    fontSize: "15px",
    color: "#27ae60",
  },
  clearSelectionButton: {
    padding: "8px 15px",
    background: "#e74c3c",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
  },
  // Empty State Styles
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  },
  emptyIcon: {
    fontSize: "64px",
    marginBottom: "20px",
    display: "block",
    opacity: "0.5",
  },
  emptyTitle: {
    fontSize: "24px",
    color: "#636e72",
    marginBottom: "12px",
  },
  emptyText: {
    color: "#b2bec3",
    fontSize: "16px",
    marginBottom: "20px",
  },
  showAllButton: {
    padding: "10px 20px",
    background: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
  },
  // Company View Styles
  companyViewContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  companyCard: {
    background: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  },
  companyHeader: {
    padding: "20px",
    background: "#f8fafc",
    borderBottom: "1px solid #e9ecef",
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  companyIcon: {
    fontSize: "24px",
  },
  companyHeaderContent: {
    flex: 1,
  },
  companyName: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#2c3e50",
    display: "block",
  },
  companySubtitle: {
    fontSize: "14px",
    color: "#7f8c8d",
    marginTop: "4px",
  },
  companyBadge: {
    background: "#3498db",
    color: "white",
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "600",
  },
  companyProjects: {
    padding: "15px",
  },
  projectItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px",
    borderBottom: "1px solid #f1f3f5",
  },
  projectItemContent: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  projectIcon: {
    fontSize: "20px",
  },
  projectName: {
    fontSize: "15px",
    color: "#495057",
  },
  projectId: {
    fontSize: "12px",
    color: "#95a5a6",
    fontFamily: "monospace",
    marginTop: "2px",
  },
  projectCalls: {
    fontSize: "14px",
    color: "#7f8c8d",
    background: "#f8f9fa",
    padding: "4px 10px",
    borderRadius: "12px",
  },
  // Hierarchy View Styles
  projectsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  projectCard: {
    background: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 6px 15px rgba(0, 0, 0, 0.07)",
  },
  projectHeader: {
    padding: "20px 24px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    borderBottom: "2px solid transparent",
  },
  projectHeaderActive: {
    background: "linear-gradient(90deg, #f1f8ff 0%, #e7f3ff 100%)",
    borderBottom: "2px solid #3498db",
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px",
  },
  iconWithText: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  projectIcon: {
    fontSize: "24px",
  },
  badge: {
    background: "linear-gradient(90deg, #3498db, #2980b9)",
    color: "white",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "600",
    boxShadow: "0 2px 4px rgba(52, 152, 219, 0.3)",
  },
  arrowIcon: {
    fontSize: "14px",
    color: "#7f8c8d",
    transition: "transform 0.2s ease",
    marginLeft: "8px",
  },
  childrenContainer: {
    padding: "16px 20px",
    background: "#fafbfc",
  },
  typeCard: {
    background: "white",
    borderRadius: "10px",
    marginBottom: "10px",
    overflow: "hidden",
    boxShadow: "0 3px 8px rgba(0, 0, 0, 0.04)",
  },
  typeHeader: {
    padding: "16px 20px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  typeHeaderActive: {
    background: "#f8fafc",
    borderLeft: "3px solid #9b59b6",
  },
  typeIcon: {
    fontSize: "20px",
  },
  typeName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#34495e",
  },
  typeId: {
    fontSize: "11px",
    color: "#95a5a6",
    fontFamily: "monospace",
    marginTop: "2px",
  },
  badgeSmall: {
    background: "#e3f2fd",
    color: "#1976d2",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: "600",
  },
  moduleCard: {
    background: "#fefefe",
    borderRadius: "8px",
    marginBottom: "8px",
    overflow: "hidden",
    border: "1px solid #edf2f7",
  },
  moduleHeader: {
    padding: "14px 18px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  moduleHeaderActive: {
    background: "#f1f8e9",
    borderLeft: "3px solid #2ecc71",
  },
  moduleIcon: {
    fontSize: "18px",
  },
  moduleName: {
    fontSize: "15px",
    fontWeight: "500",
    color: "#2d3436",
  },
  moduleId: {
    fontSize: "11px",
    color: "#95a5a6",
    fontFamily: "monospace",
    marginTop: "2px",
  },
  complaintsContainer: {
    background: "white",
    borderRadius: "8px",
    padding: "16px",
    marginTop: "4px",
    border: "1px solid #e9ecef",
  },
  complaintsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #f1f3f5",
  },
  complaintsTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#2c3e50",
  },
  complaintsCount: {
    fontSize: "14px",
    color: "#7f8c8d",
    fontWeight: "500",
  },
  complaintsList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  complaintCard: {
    background: "#f8f9fa",
    borderRadius: "8px",
    padding: "12px 16px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "1px solid transparent",
  },
  complaintCardActive: {
    background: "white",
    border: "1px solid #dfe6e9",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
  },
  complaintHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px",
  },
  complaintIconText: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  complaintIcon: {
    fontSize: "16px",
  },
  complaintCallNo: {
    fontSize: "15px",
    fontWeight: "500",
    color: "#2d3436",
  },
  companyNameSmall: {
    fontSize: "12px",
    color: "#7f8c8d",
    marginTop: "2px",
  },
  arrowIconSmall: {
    fontSize: "12px",
    color: "#95a5a6",
  },
  complaintDetails: {
    marginTop: "12px",
    padding: "12px",
    background: "#f8f9fa",
    borderRadius: "6px",
    borderTop: "1px solid #e9ecef",
  },
  detailRow: {
    display: "flex",
    marginBottom: "8px",
    flexWrap: "wrap",
  },
  detailLabel: {
    fontWeight: "600",
    color: "#495057",
    minWidth: "100px",
    fontSize: "14px",
  },
  detailValue: {
    color: "#6c757d",
    fontSize: "14px",
    flex: 1,
    wordBreak: "break-word",
  },
  complaintFooter: {
    marginTop: "10px",
    paddingTop: "10px",
    borderTop: "1px dashed #dee2e6",
    display: "flex",
    justifyContent: "flex-end",
  },
  indexBadge: {
    background: "#3498db",
    color: "white",
    padding: "4px 8px",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "600",
  },
  noData: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    padding: "24px",
    background: "#f8f9fa",
    borderRadius: "8px",
    color: "#95a5a6",
  },
  noDataIcon: {
    fontSize: "20px",
  },
  noDataText: {
    fontSize: "15px",
    fontWeight: "500",
  },
  noComplaints: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
    gap: "12px",
    color: "#bdc3c7",
  },
  noComplaintsIcon: {
    fontSize: "40px",
    opacity: "0.5",
  },
  noComplaintsText: {
    fontSize: "15px",
  },
};